import { Sides } from "@project/emerge-types";
import partition from "lodash/partition";
import sum from "lodash/sum";
import times from "lodash/times";
import { Vector3 } from "three";
import type { Axis } from "../enum/Axis";
import { BranchOrientation } from "../enum/BranchOrientation";
import { InteriorType } from "../enum/InteriorType";
import { NodeType } from "../enum/NodeType";
import { Orientation } from "../enum/Orientation";
import type { CaseInteriorOptions } from "../interfaces/AccessoryCompartmentOptions";
import type { CompiledCaseInterior } from "../interfaces/CompiledCaseInterior";
import type { ContentItem } from "../interfaces/ContentItem";
import type { ContentNode } from "../interfaces/ContentNode";
import type { OptimizationParameters } from "../interfaces/OptimizationParameters";
import type { PaddingCube } from "../interfaces/PaddingCube";
import type { Root } from "../interfaces/Root";
import type { SubBranch } from "../interfaces/SubBranch";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import { createNewCompartment, type Nodes } from "../structure/Compartment";
import type { PaddingMap } from "../user_options/InteriorOptions";
import { mutations } from "./mutations";

export function createTrivialSolution(
  content: ContentItem,
  tolerance: { x: number; y: number; z: number },
  trayMaterial: TrayMaterialType,
  upperPaddingAlignment: boolean,
): CaseInteriorOptions {
  return {
    tolerance,
    trayMaterial,
    tree: createSingleItemTree(content),
    type: InteriorType.CASE,
    upperPaddingAlignment,
  };
}

// mutate an individual
export function mutateTree(entity: Root): Root {
  for (let n = 0; n < 3; n++) {
    const m = randomElement(Object.values(mutations));
    if (m) {
      m.mutation(entity);
    }
  }
  return entity;
}

export function randomInt(max: number): number {
  return Math.floor(Math.random() * max);
}

export function randomElement<T>(array: readonly T[]): T | undefined {
  if (array.length === 0) return undefined;
  const id = randomInt(array.length);
  return array[id];
}

// seed function to create new individuals
export function buildTree(contents: ContentItem): Root;
export function buildTree(contents: ContentItem[]): Root;
export function buildTree(contents: ContentItem | ContentItem[]): Root {
  const list = Array.isArray(contents) ? contents : [contents];
  const root: Root = {
    age: 0,
    children: [],
    fitness: 0,
    orientation: BranchOrientation.Horizontal,
    type: NodeType.BRANCH,
  };

  // stash each item into the grid
  for (const item of list) {
    for (let count = 0; count < item.count; count++) {
      root.children.push({
        item,
        orientation: 0,
        type: NodeType.ITEM,
      });
    }
  }
  return root;
}

export function calculateNodeInner(node: ContentNode): Vector3 {
  type DimKey = Extract<keyof ContentItem, "width" | "height" | "depth">;
  const keys = [
    "width",
    "height",
    "depth",
  ] as const satisfies readonly DimKey[];
  const map = baseChangeMap[node.orientation as Orientation];
  const k0 = keys[map[0]];
  const k1 = keys[map[1]];
  const k2 = keys[map[2]];
  return new Vector3(node.item[k0], node.item[k1], node.item[k2]);
}

type AxisPermutation = readonly [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2];
const baseChangeMap: Record<Orientation, AxisPermutation> = {
  [Orientation.NORMAL]: [0, 1, 2],
  [Orientation.XTOZ_ZTOX]: [2, 1, 0],
  [Orientation.XTOY_YZOX]: [1, 0, 2],
  [Orientation.YTOZ_ZTOY]: [0, 2, 1],
  [Orientation.XTOZ_ZTOY_YTOX]: [1, 2, 0],
  [Orientation.XTOY_YTOZ_ZTOX]: [2, 0, 1],
};

export function reorient(orientation: Orientation, side: Sides): Sides {
  const base: Axis = Math.floor(side / 2);
  const variant = side % 2;
  return baseChangeMap[orientation][base] * 2 + variant;
}

export function reorientPadding(
  orientation: Orientation,
  padding: PaddingMap,
): PaddingMap {
  return {
    [Sides.BACK]: padding[reorient(orientation, Sides.BACK)],
    [Sides.FRONT]: padding[reorient(orientation, Sides.FRONT)],
    [Sides.LEFT]: padding[reorient(orientation, Sides.LEFT)],
    [Sides.RIGHT]: padding[reorient(orientation, Sides.RIGHT)],
    [Sides.TOP]: padding[reorient(orientation, Sides.TOP)],
    [Sides.BOTTOM]: padding[reorient(orientation, Sides.BOTTOM)],
  };
}

/**
 * Additional-padding cubes are authored in the 2D editor relative to the
 * item's own (unrotated) width/depth. When the optimizer rotates the
 * compartment (e.g. the 90° width<->depth swap), the cube offsets and
 * footprint must follow the same axis permutation as the compartment itself
 * (see calculateNodeInner), otherwise the foam ends up mispositioned in 3D.
 *
 * The cube's in-plane offset is (x along width, y along depth) and its
 * footprint is (width along width-axis, depth along depth-axis). CaseInterior
 * adds `(cube.x, 0, cube.y)` as world X/Z offsets from the cavity centre, so
 * reorient must express the authored item-local offset in world axes after the
 * optimizer turn.
 *
 * Sizes may follow the plain axis permutation of `baseChangeMap` because they
 * are unsigned, but offsets may not: swapping two axes has determinant -1, so
 * it mirrors the layout instead of turning it, which flips the handedness of a
 * multi-cube arrangement (a block in front of the item lands beside it). The
 * width↔depth turn (XTOZ_ZTOX) is a 90° rotation about the vertical axis, i.e.
 * `(ox, oy) → (oy, -ox)`: e.g. (100, -50) maps to (-50, -100).
 *
 * `cube.side` (bottom = Korpus, top = Deckel) is a case-vertical layer choice, not
 * a wall direction; in-plane optimizer turns do not remap it.
 */
export function reorientPaddingCubes(
  orientation: Orientation,
  cubes: PaddingCube[] | undefined,
): PaddingCube[] {
  if (!cubes?.length) return cubes ?? [];
  if (orientation === Orientation.NORMAL) return cubes;
  if (orientation === Orientation.XTOZ_ZTOX) {
    return cubes.map((cube) => ({
      ...cube,
      x: cube.y,
      // `|| 0` keeps a centred cube at 0 rather than -0.
      y: -cube.x || 0,
      width: cube.depth,
      depth: cube.width,
    }));
  }
  const map = baseChangeMap[orientation];
  return cubes.map((cube) => {
    // Indexed as [width-axis, height-axis, depth-axis] to match calculateNodeInner.
    const offset = [cube.x, 0, cube.y] as const;
    const dims = [cube.width, cube.height, cube.depth] as const;
    return {
      ...cube,
      x: offset[map[0]],
      y: offset[map[2]],
      width: dims[map[0]],
      depth: dims[map[2]],
    };
  });
}

// calculate size of item with padding and tolerance
export function calculateNodeOuter(
  node: ContentNode,
  tolerance: Tolerances,
): Vector3 {
  const inner = calculateNodeInner(node);
  const padding = reorientPadding(node.orientation, node.item.padding);
  return new Vector3(
    padding[Sides.LEFT] + padding[Sides.RIGHT] + tolerance.x,
    Math.max(padding[Sides.BOTTOM], node.item.paddingExtraForBottom) +
      tolerance.y,
    padding[Sides.FRONT] + padding[Sides.BACK] + tolerance.z,
  ).add(inner);
}

function iterateChildren(
  node: SubBranch,
  iteratee: (child: Nodes, n: number) => void,
) {
  node.children.forEach(iteratee);
}

type GridSizes = {
  primaries: number[];
  maxHeight: number;
  maxSecondary: number;
};
function calculateGridSize(
  node: SubBranch,
  panelStrength: number,
  tolerance: Tolerances,
): GridSizes {
  const primaries: number[] = [];
  let maxHeight = 0;
  let maxSecondary = 0;
  const primary: keyof Vector3 =
    node.orientation === BranchOrientation.Horizontal ? "x" : "z";
  const secondary: keyof Vector3 =
    node.orientation !== BranchOrientation.Horizontal ? "x" : "z";
  iterateChildren(node, (child, n) => {
    if (!child) {
      console.error(`calculateGridSize::iterateChildren`, node, child);
    }
    const childSize = calculateNodeSize(child, panelStrength, tolerance);
    const currentPrimary = primaries[n] ?? 0;
    if (currentPrimary <= childSize[primary]) {
      primaries[n] = childSize[primary];
    }
    if (maxHeight < childSize.y) {
      maxHeight = childSize.y;
    }
    if (maxSecondary < childSize[secondary]) {
      maxSecondary = childSize[secondary];
    }
  });
  return { maxHeight, maxSecondary, primaries };
}
export type Tolerances = { x: number; y: number; z: number };
function calculateNodeSize(
  node: Nodes,
  panelStrength: number,
  tolerance: Tolerances,
): Vector3 {
  switch (node.type) {
    case NodeType.ITEM: {
      if (node.item.forceOrientation) {
        node.orientation %= 2;
      }
      return calculateNodeOuter(node, tolerance);
    }
    case NodeType.BRANCH: {
      const { primaries, maxHeight, maxSecondary } = calculateGridSize(
        node,
        panelStrength,
        tolerance,
      );
      return node.orientation === BranchOrientation.Horizontal
        ? new Vector3(
            sum(primaries) + (primaries.length - 1) * panelStrength,
            maxHeight,
            maxSecondary,
          )
        : new Vector3(
            maxSecondary,
            maxHeight,
            sum(primaries) + (primaries.length - 1) * panelStrength,
          );
    }
    default:
      return new Vector3();
  }
}

// function to calculate the dimensions of a solution
export function calculateTree(
  tree: Root,
  panelStrength: number,
  tolerance: Tolerances,
): CompiledCaseInterior {
  // calculateTree mutates the tree in place (orientation normalization in
  // calculateNodeSize, child splicing in simplifyBranch). When the tree comes
  // straight from the (frozen) form/store state this throws
  // `"orientation" is read-only`. Work on a private copy so callers' input is
  // never mutated. The tree is plain serialisable data (Vector3 instances are
  // only produced into the result, never stored on nodes), so structuredClone
  // is safe here.
  tree = structuredClone(tree);
  const result: CompiledCaseInterior = {
    panels: [],
    volumes: [],
  };
  function generateContent(
    node: Nodes,
    center: Vector3 = new Vector3(),
    parentSize: Vector3 = calculateNodeSize(node, panelStrength, tolerance),
  ) {
    const ownSize = calculateNodeSize(node, panelStrength, tolerance);
    switch (node.type) {
      case NodeType.BRANCH: {
        let { primaries, maxSecondary, maxHeight } = calculateGridSize(
          node,
          panelStrength,
          tolerance,
        );
        const primaryDimension: keyof Vector3 =
          node.orientation === BranchOrientation.Horizontal ? "x" : "z";
        const secondaryDimension: keyof Vector3 =
          node.orientation !== BranchOrientation.Horizontal ? "x" : "z";
        // how much gap is there due to this branch beeing smaller than its neighbours
        let primGap = parentSize[primaryDimension] - ownSize[primaryDimension];
        if (primGap > 0) {
          if (primGap > 100) {
            // accessory compartment
            const n = node.children.length;
            const panelOffset =
              sum(primaries.slice(0, n)) -
              ownSize[primaryDimension] / 2 -
              primGap / 2 +
              (n - 0.5) * panelStrength;
            const panelCenter = center.clone();
            panelCenter[primaryDimension] += panelOffset;
            result.panels.push({
              branchOrientation: node.orientation,
              center: panelCenter,
              crossSections: [],
              width: maxSecondary,
            });
          } else {
            // spread extra over primaries
            ownSize[primaryDimension] += primGap;
            primaries[0] = (primaries[0] ?? 0) + primGap;
            primGap = 0;
          }
        }
        maxSecondary = parentSize[secondaryDimension];
        iterateChildren(node, (child: Nodes, n: number) => {
          // calculate node offset to parent
          const offset = new Vector3();
          const primN = primaries[n] ?? 0;
          offset[primaryDimension] =
            sum(primaries.slice(0, n)) + // all compartments in branch so far
            primN / 2 - // half of this compartment
            ownSize[primaryDimension] / 2 - // half of the branches size to start at left side
            primGap / 2 +
            n * panelStrength; // all the panels so far
          // create seperator panel
          if (n > 0) {
            const panelOffset =
              sum(primaries.slice(0, n)) -
              ownSize[primaryDimension] / 2 -
              primGap / 2 +
              (n - 0.5) * panelStrength;
            const panelCenter = center.clone();
            panelCenter[primaryDimension] += panelOffset;
            result.panels.push({
              branchOrientation: node.orientation,
              center: panelCenter,
              crossSections: [],
              width: maxSecondary,
            });
          }
          // recurse
          generateContent(
            child,
            center.clone().add(offset),
            node.orientation === BranchOrientation.Horizontal
              ? new Vector3(primaries[n], maxHeight, maxSecondary)
              : new Vector3(maxSecondary, maxHeight, primaries[n]),
          );
        });
        break;
      }
      case NodeType.ITEM: {
        // create volume
        const reorientedPadding = reorientPadding(
          node.orientation,
          node.item.padding,
        );
        const paddingSize = new Vector3(
          reorientedPadding[Sides.LEFT] + reorientedPadding[Sides.RIGHT],
          reorientedPadding[Sides.BOTTOM],
          reorientedPadding[Sides.BACK] + reorientedPadding[Sides.FRONT],
        );
        const difference = parentSize.clone().sub(ownSize);
        reorientedPadding[Sides.LEFT] += difference.x / 2;
        reorientedPadding[Sides.RIGHT] += difference.x / 2;
        reorientedPadding[Sides.FRONT] += difference.z / 2;
        reorientedPadding[Sides.BACK] += difference.z / 2;
        reorientedPadding[Sides.BOTTOM] += difference.y;
        result.volumes.push({
          additionalPaddingCubes: reorientPaddingCubes(
            node.orientation,
            node.item.paddingCubes,
          ),
          center,
          padding: reorientedPadding,
          sizeWithPadding: parentSize,
          sizeWithoutPadding: ownSize.clone().sub(paddingSize),
        });
        break;
      }
    }
  }
  generateContent(tree);
  combinePanels(result, panelStrength);
  intersectPanels(result, panelStrength);
  return result;
}

/**
 * Similar to calculateTree but works directly with heights/widths arrays
 * Used by the Facheditor (Ebenen/Unterteilungen) mode
 */
export function calculateCaseInteriorFromHeightsWidths(
  heights: number[],
  widths: number[][],
  depth: number,
  panelStrength: number,
  tolerance: Tolerances,
): CompiledCaseInterior {
  const result: CompiledCaseInterior = {
    panels: [],
    volumes: [],
  };

  // Defensive checks: provide defaults if undefined
  if (!heights || heights.length === 0) {
    heights = [500];
  }
  if (!widths || widths.length === 0) {
    widths = [[500]];
  }

  // Calculate total height
  const totalHeight = heights.reduce((sum, h) => sum + h, 0);

  // Current Y position (starting from top)
  let currentY = totalHeight / 2;

  // Process each level
  for (let levelIdx = 0; levelIdx < heights.length; levelIdx++) {
    const levelHeight = heights[levelIdx] ?? 500;
    const levelWidths = widths[levelIdx] ?? [500];

    // Calculate total width for this level
    const totalWidth = levelWidths.reduce((sum, w) => sum + w, 0);

    // Current X position (starting from left)
    let currentX = -totalWidth / 2;

    // Process each compartment in this level
    for (let compIdx = 0; compIdx < levelWidths.length; compIdx++) {
      const compWidth = levelWidths[compIdx] ?? 500;

      // Create volume for this compartment
      const sizeWithoutPadding = new Vector3(
        compWidth - 2 * tolerance.x,
        levelHeight - tolerance.y,
        depth - 2 * tolerance.z,
      );
      const sizeWithPadding = new Vector3(compWidth, levelHeight, depth);
      const center = new Vector3(
        currentX + compWidth / 2,
        currentY - levelHeight / 2,
        0,
      );

      // Im Facheditor-Modus wird kein Padding aus der Tolerance erstellt
      // Die Tolerance dient nur zur Größenanpassung (sizeWithoutPadding wird kleiner)
      // Padding sollte 0 sein, da keine Padding-Objekte erstellt werden sollen
      const padding: PaddingMap = {
        [Sides.LEFT]: 0,
        [Sides.RIGHT]: 0,
        [Sides.FRONT]: 0,
        [Sides.BACK]: 0,
        [Sides.BOTTOM]: 0,
        [Sides.TOP]: 0,
      };

      result.volumes.push({
        center,
        padding,
        sizeWithPadding,
        sizeWithoutPadding,
        additionalPaddingCubes: [],
      });

      // Add vertical panel if not the last compartment
      if (compIdx < levelWidths.length - 1) {
        const panelCenter = new Vector3(
          currentX + compWidth,
          currentY - levelHeight / 2,
          0,
        );
        result.panels.push({
          branchOrientation: BranchOrientation.Vertical,
          center: panelCenter,
          crossSections: [],
          width: depth,
        });
      }

      currentX += compWidth;
    }

    // Add horizontal panel if not the last level
    if (levelIdx < heights.length - 1) {
      const totalWidthForPanel = Math.max(
        ...widths
          .slice(0, levelIdx + 1)
          .map((w) => w.reduce((sum, width) => sum + width, 0)),
      );
      const panelCenter = new Vector3(0, currentY - levelHeight, 0);
      result.panels.push({
        branchOrientation: BranchOrientation.Horizontal,
        center: panelCenter,
        crossSections: [],
        width: totalWidthForPanel,
      });
    }

    currentY -= levelHeight;
  }

  combinePanels(result, panelStrength);
  intersectPanels(result, panelStrength);
  return result;
}

// the genetic algorithm may cause unreasonable results, this function cleans such inefficiencies
export function simplifyTree(tree: Root): Root {
  function simplifyBranch(node: SubBranch) {
    for (let c = 0; c < node.children.length; c++) {
      const child = node.children[c];
      if (!child) {
        continue;
      }
      if (child.type === NodeType.BRANCH) {
        simplifyBranch(child);
        const len = child.children.length;
        if (len === 0) {
          node.children.splice(c, 1);
        } else if (len === 1) {
          const onlyChild = child.children[0];
          if (onlyChild) {
            node.children[c] = onlyChild;
          } else {
            node.children.splice(c, 1);
          }
        } else if (node.orientation === child.orientation) {
          node.children.splice(c, 1, ...child.children);
        }
      }
    }
  }
  simplifyBranch(tree);
  return tree;
}

// Helper functions for combinePanels
function sameOrientation(
  a: CompiledCaseInterior["panels"][number],
  b: CompiledCaseInterior["panels"][number],
): boolean {
  return a.branchOrientation === b.branchOrientation;
}

function sameAxis(
  a: CompiledCaseInterior["panels"][number],
  b: CompiledCaseInterior["panels"][number],
): boolean {
  const coord2 =
    a.branchOrientation === BranchOrientation.Horizontal ? "x" : "z";
  return a.center[coord2] === b.center[coord2];
}

function isAdjacentWithGap(
  a: CompiledCaseInterior["panels"][number],
  b: CompiledCaseInterior["panels"][number],
  gap: number,
): boolean {
  const coord =
    a.branchOrientation === BranchOrientation.Horizontal ? "z" : "x";
  return (
    Math.abs(a.center[coord] - b.center[coord]) ===
    (a.width + b.width) / 2 + gap
  );
}

function mergePanelInto(
  a: CompiledCaseInterior["panels"][number],
  b: CompiledCaseInterior["panels"][number],
  gap: number,
) {
  a.width += b.width + gap;
  const dir = a.center.clone().sub(b.center).normalize();
  a.center.add(dir.multiplyScalar(-(b.width + gap) / 2));
}

// combine panels that touch each other
export function combinePanels(
  tree: CompiledCaseInterior,
  panelStrength: number,
) {
  const { panels } = tree;
  if (!panels) return;

  const findPartnerIndex = (list: typeof panels, i: number): number | null => {
    const a = list[i];
    if (!a) return null;
    for (let j = i + 1; j < list.length; j++) {
      const b = list[j];
      if (!b) continue;
      if (!sameOrientation(a, b)) continue;
      if (!sameAxis(a, b)) continue;
      if (!isAdjacentWithGap(a, b, panelStrength)) continue;
      return j;
    }
    return null;
  };

  const findMergeablePair = (list: typeof panels): [number, number] | null => {
    for (let i = 0; i < list.length; i++) {
      const j = findPartnerIndex(list, i);
      if (j !== null) return [i, j];
    }
    return null;
  };

  // Iteratively merge pairs until no more merges are possible
  while (true) {
    const pair = findMergeablePair(panels);
    if (!pair) break;
    const [i, j] = pair;
    const a = panels[i];
    const b = panels[j];
    if (!a || !b) break;
    mergePanelInto(a, b, panelStrength);
    panels.splice(j, 1);
  }
}

export function intersectPanels(
  tree: CompiledCaseInterior,
  _panelStrength: number,
): void {
  const [horizontalPanels, verticalPanels] = partition(
    tree.panels,
    (panel) => panel.branchOrientation === BranchOrientation.Horizontal,
  );

  for (const hPanel of horizontalPanels) {
    for (const vPanel of verticalPanels) {
      const diff = vPanel.center.clone().sub(hPanel.center.clone());
      if (Math.abs(diff.z) > hPanel.width / 2) {
        continue;
      }
      if (Math.abs(diff.x) > vPanel.width / 2) {
        continue;
      }
      const crossSection = new Vector3(hPanel.center.x, 0, vPanel.center.z);
      vPanel.crossSections.push(crossSection);
      hPanel.crossSections.push(crossSection);
    }
  }
}

export function createSingleItemTree(
  item: ContentItem = createNewCompartment(),
): Root {
  return {
    children: times(item.count, () => ({
      item,
      orientation: 0,
      type: NodeType.ITEM,
    })),
    fitness: 0,
    orientation: BranchOrientation.Horizontal,
    age: 0,
    type: NodeType.BRANCH,
  };
}
// function that tells the genetic algorithm when to stop
export function convertList(
  list: ContentItem[],
  _optimizationParameters: OptimizationParameters,
): ContentItem[] {
  return list;
}

export const TestTree: Root = {
  children: [
    {
      children: [
        {
          item: createNewCompartment(),
          orientation: 0,
          type: NodeType.ITEM,
        },
        {
          item: createNewCompartment(300, 200, 350),
          orientation: 0,
          type: NodeType.ITEM,
        },
      ],
      orientation: BranchOrientation.Vertical,
      type: NodeType.BRANCH,
    },
    {
      item: createNewCompartment(),
      orientation: 0,
      type: NodeType.ITEM,
    },
    {
      item: createNewCompartment(),
      orientation: 0,
      type: NodeType.ITEM,
    },
  ],
  fitness: 0,
  orientation: BranchOrientation.Horizontal,
  age: 0,
  type: NodeType.BRANCH,
};
